import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Ban, Trash2, Link2, Loader2 } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { naira } from "@/components/site/ShortletCard";

const BADGE = {
  pending: "bg-amber-100 text-amber-900", approved: "bg-sky-100 text-sky-900", paid: "bg-emerald-100 text-emerald-900",
  declined: "bg-zinc-200 text-zinc-800", cancelled: "bg-zinc-200 text-zinc-800", expired: "bg-orange-100 text-orange-900",
  blocked: "bg-gw-tint text-gw-deep",
};
const input = "mt-1 w-full border border-gw-ground/30 p-2 bg-white";

const BlockForm = ({ shortlets, onDone }) => {
  const [f, setF] = useState({ shortlet_id: "", check_in: "", check_out: "", note: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await http.post("/bookings/block", f); toast.success("Dates blocked"); setF((x) => ({ ...x, check_in: "", check_out: "", note: "" })); onDone(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-5 gap-3 items-end border border-gw-ground/15 p-4 mb-8" data-testid="admin-block-form">
      <label className="lab text-xs sm:col-span-2">Apartment
        <select required value={f.shortlet_id} onChange={set("shortlet_id")} className={input} data-testid="admin-block-shortlet-select">
          <option value="">Choose…</option>
          {shortlets.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
        </select>
      </label>
      <label className="lab text-xs">From<input type="date" required value={f.check_in} onChange={set("check_in")} className={input} data-testid="admin-block-from-input" /></label>
      <label className="lab text-xs">To<input type="date" required value={f.check_out} onChange={set("check_out")} className={input} data-testid="admin-block-to-input" /></label>
      <button type="submit" disabled={busy} className="lab bg-gw-ground text-white px-4 py-2.5 inline-flex items-center justify-center gap-2 hover:bg-gw-deep transition-colors duration-200 disabled:opacity-60" data-testid="admin-block-submit-button">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}Block dates
      </button>
      <input placeholder="Note, e.g. booked on another site" value={f.note} onChange={set("note")} maxLength={300} className={`${input} sm:col-span-5`} data-testid="admin-block-note-input" />
    </form>
  );
};

const Actions = ({ b, i, act, remove }) => {
  const btn = "p-2 hover:bg-gw-tint transition-colors duration-200";
  const copy = () => { navigator.clipboard?.writeText(`${window.location.origin}/booking/${b.pay_token}`); toast.success("Payment link copied"); };
  return (
    <div className="flex items-center gap-1 shrink-0">
      {["pending", "expired"].includes(b.status) && <button type="button" title="Approve" onClick={() => act(b, "approve")} className={`${btn} text-emerald-700`} data-testid={`admin-booking-approve-${i}`}><Check size={16} /></button>}
      {["pending", "approved", "expired"].includes(b.status) && <button type="button" title="Decline" onClick={() => act(b, "decline")} className={btn} data-testid={`admin-booking-decline-${i}`}><X size={16} /></button>}
      {b.status === "approved" && b.pay_token && <button type="button" title="Copy payment link" onClick={copy} className={btn} data-testid={`admin-booking-copy-link-${i}`}><Link2 size={16} /></button>}
      {["paid", "blocked", "approved"].includes(b.status) && <button type="button" title="Cancel" onClick={() => act(b, "cancel")} className={btn} data-testid={`admin-booking-cancel-${i}`}><Ban size={16} /></button>}
      {b.status !== "paid" && <button type="button" title="Delete" onClick={() => remove(b)} className={`${btn} text-destructive`} data-testid={`admin-booking-delete-${i}`}><Trash2 size={16} /></button>}
    </div>
  );
};

export const BookingsPanel = () => {
  const [items, setItems] = useState(null);
  const [shortlets, setShortlets] = useState([]);
  const load = useCallback(() => http.get("/bookings").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([])), []);
  useEffect(() => { load(); http.get("/shortlets").then((r) => setShortlets(r.data || [])).catch(() => {}); }, [load]);

  const act = async (b, action) => {
    try {
      await http.patch(`/bookings/${b.id}`, { action });
      toast.success(action === "approve" ? "Approved — the guest has been emailed a payment link" : `Booking ${action === "decline" ? "declined" : "cancelled"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (b) => {
    if (!window.confirm("Delete this booking record?")) return;
    try { await http.delete(`/bookings/${b.id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-bookings-panel">
      <p className="text-gw-ground/80 mb-4">Approve a request to hold the dates for 48 hours and email the guest a payment link. Paid bookings are confirmed automatically. Block dates you've let elsewhere.</p>
      <BlockForm shortlets={shortlets} onDone={load} />
      <div className="border-t border-gw-ground/20">
        {(items || []).map((b, i) => (
          <div key={b.id} className="flex flex-wrap md:flex-nowrap items-center gap-4 py-4 border-b border-gw-ground/20" data-testid={`admin-booking-row-${i}`}>
            <span className={`lab text-[10px] px-2 py-1 ${BADGE[b.status] || ""}`} data-testid={`admin-booking-status-${i}`}>{b.status}</span>
            <div className="min-w-0 flex-1">
              <p className="lux text-lg truncate">{b.shortlet_title} · {b.check_in} → {b.check_out}</p>
              <p className="text-sm text-gw-ground/70 truncate">
                {b.status === "blocked" ? b.note || "Blocked by admin" : [b.name, b.email, b.phone, `${b.nights} nights · ${b.guests} guests`, naira(b.total), b.provider && `paid via ${b.provider}`].filter(Boolean).join(" · ")}
              </p>
              {b.note && b.status !== "blocked" && <p className="text-sm text-gw-ground/70 italic truncate">“{b.note}”</p>}
            </div>
            <Actions b={b} i={i} act={act} remove={remove} />
          </div>
        ))}
        {items?.length === 0 && <p className="py-10 text-gw-ground/70" data-testid="admin-bookings-empty">No booking requests yet.</p>}
      </div>
    </div>
  );
};
